import {
  createApiFactory,
  createApiRef,
  createPlugin,
  createRoutableExtension,
  discoveryApiRef,
  fetchApiRef,
} from '@mainstage/core-plugin-api';
import { AccessControlListClient } from '@agilelab/plugin-wb-access-control-common';
import {
  accessControlListApiRef,
  actionHandlerApiRef,
  ActionHandlerClient,
} from '@agilelab/plugin-wb-access-control';
import { dataProductRouteRef, rootRouteRef } from './routes';
import { metricsApiRef } from '@agilelab/plugin-wb-metrics';
import { DefaultMetricsClient } from '@agilelab/plugin-wb-metrics-common';
import {
  BusinessConceptsApi,
  MarketplaceApi,
} from '@agilelab/plugin-wb-marketplace-common';
import { knowledgeGraphManagerApiRef } from '@agilelab/plugin-wb-scaffolder';
import {
  BusinessConceptsClient,
  KnowledgeGraphManagerClient,
} from './components/BusinessConceptMap/api';

export { knowledgeGraphManagerApiRef };

export const marketplaceApiRef = createApiRef<MarketplaceApi>({
  id: 'marketplace',
});

/**
 * Frontend-only API ref for the vocabulary-neutral Business Concepts API,
 * used by the Business Concept Map. Kept separate from
 * `knowledgeGraphManagerApiRef`, which remains available for non-Map
 * consumers of the raw Knowledge Graph Manager API.
 */
export const businessConceptsApiRef = createApiRef<BusinessConceptsApi>({
  id: 'wb-marketplace.business-concepts',
});

export const marketplacePlugin = createPlugin({
  id: 'wb-marketplace',
  routes: {
    root: rootRouteRef,
    marketplaceProductRef: dataProductRouteRef,
  },
  apis: [
    createApiFactory({
      api: metricsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new DefaultMetricsClient({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: actionHandlerApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new ActionHandlerClient({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: accessControlListApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new AccessControlListClient({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: knowledgeGraphManagerApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new KnowledgeGraphManagerClient({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: businessConceptsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new BusinessConceptsClient({ discoveryApi, fetchApi }),
    }),
  ],
});

export const MarketplacePage = marketplacePlugin.provide(
  createRoutableExtension({
    name: 'MarketplacePage',
    component: () => import('./components/Router').then(m => m.Router),
    mountPoint: rootRouteRef,
  }),
);
